import { Product } from '@shared/components/ui/Product';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationBatchBadge } from '../components/display/LocationBatchBadge';
import { DropdownActions } from '../components/interaction/DropdownActions';
import { getPrimaryDisplayableStockBatch, Location } from '../models/Location';

// 🎯 Función utilitaria para renderizar valores de forma segura
const safeRender = (value: any): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value.toString();

  // Si es un objeto, intentar extraer propiedades comunes
  if (typeof value === 'object') {
    if (value.name) return value.name;
    if (value.description) return value.description;
    if (value.title) return value.title;
    if (value.label) return value.label;
    if (value.id) return value.id.toString();
  }

  return '-';
};

// 🎯 Función para convertir ColDef de AG Grid a ColumnDef de TanStack para ColumnSelector
const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<Location>[] => {
  return agGridColumns
    .filter((col) => {
      // 🎯 Excluir columnas que no deben ser opcionales
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
      cell: col.cellRenderer ? () => null : undefined, // ColumnSelector no necesita cell renderers
    }));
};

// 🎯 Tipos para los handlers
interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  hasTeamData?: boolean;
  onLocationEdit?: (location: Location) => void;
  onLocationDelete?: (location: Location) => void;
  onLocationAdjust?: (location: Location) => void;
  onLocationPrint?: (location: Location, size: 'small' | 'large') => void;
  onLocationToggleLock?: (location: Location) => void;
}

// 🎯 Hook para manejar las columnas de AG Grid y ColumnSelector
export const useAgGridColumns = ({
  selectedColumns = [],
  hasTeamData = false,
  onLocationEdit,
  onLocationDelete,
  onLocationAdjust,
  onLocationPrint,
  onLocationToggleLock,
}: UseAgGridColumnsProps) => {
  const { t } = useTranslation('locationsModule');

  // 🎯 Función para obtener la traducción del tipo de ubicación
  const getLocationTypeTranslation = (typeName: string | undefined) => {
    if (!typeName) return t('location_types.not_available');

    // Mapear los tipos de la API a las claves de traducción
    const typeMap: Record<string, string> = {
      Almacen: 'location_types.warehouse',
      Almacén: 'location_types.warehouse',
      Warehouse: 'location_types.warehouse',
      Picking: 'location_types.picking',
    };

    const translationKey = typeMap[typeName];
    return translationKey ? t(translationKey) : typeName;
  };

  // 🎯 Definiciones completas de columnas para AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // 🔵 COLUMNA: Código
      {
        field: 'code',
        headerName: t('table.code'),
        sortable: true, // 🎯 Marcar como sortable para que AgGridTable aplique el sorting automáticamente
        // 🎯 CELDA: Muestra el código con ícono de candado si está bloqueada
        cellRenderer: (params: any) => {
          const location = params.data as Location;
          return (
            <div className="flex items-center gap-2">
              {location.is_locked && (
                <FontAwesomeIcon icon={faLock} className="text-complementary-text text-sm" title={t('status.locked')} />
              )}
              <span className="text-sm font-medium">{location.code}</span>
            </div>
          );
        },
      },

      // 🔵 COLUMNA: Tipo de ubicación
      {
        headerName: t('table.location_type'),
        field: 'type.name',
        sortable: false,
        valueGetter: (params) => params.data.type?.name,
        // 🎯 CELDA: Tipo de ubicación traducido
        cellRenderer: (params: any) => {
          const location = params.data as Location;
          return <span className="text-sm capitalize">{getLocationTypeTranslation(location.type?.name)}</span>;
        },
      },

      // 🔵 COLUMNA: Producto
      {
        headerName: t('table.product'),
        field: 'product.name',
        sortable: false,
        valueGetter: (params) => params.data.product?.name,
        // 🎯 CELDA: Componente Product con imagen
        cellRenderer: (params: any) => {
          const location = params.data as Location;
          // 🎯 Verificar que producto existe antes de renderizar
          if (!location.product?.name) {
            return null;
          }
          return (
            <Product
              src={location.product.image || ''}
              name={location.product.name}
              id={location.product.id}
              code={location.product.code || ''}
              enableLineClamp={false}
              showTooltip={true}
              showImageModal={true}
            />
          );
        },
      },
    ];

    if (hasTeamData) {
      baseColumns.push({
        headerName: t('table.client'),
        field: 'product.team.name',
        sortable: false,
        valueGetter: (params) => params.data.product?.team?.name,
        cellRenderer: (params: any) => {
          const location = params.data as Location;
          return <span className="text-sm">{safeRender(location.product?.team?.name)}</span>;
        },
      });
    }

    baseColumns.push(
      // 🔵 COLUMNA: Stock
      {
        headerName: t('table.stock'),
        field: 'stock',
        sortable: false,
        // 🎯 CELDA: Cantidad de stock
        cellRenderer: (params: any) => {
          const location = params.data as Location;
          return <span className="text-sm">{safeRender(location.stock)}</span>;
        },
      },

      // 🔵 COLUMNA: Lote (primer stock_batches; una ubicación = un lote visible)
      {
        headerName: t('table.batch'),
        field: 'stock_batches',
        sortable: false,
        flex: 1,
        minWidth: 140,
        cellRenderer: (params: any) => {
          const location = params.data as Location;
          const batch = getPrimaryDisplayableStockBatch(location);
          if (!batch) {
            return undefined;
          }
          return <LocationBatchBadge batch={batch} className="w-full justify-start sm:inline-flex" />;
        },
      },
    );

    // 🔵 COLUMNA: Acciones (siempre presente)
    baseColumns.push({
      headerName: '',
      field: 'actions',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right', // Evita que el usuario mueva la columna
      suppressMovable: true, // Deshabilita el drag & drop para esta columna
      suppressSizeToFit: true,
      suppressAutoSize: true,
      // 🎯 CELDA: Dropdown de acciones
      cellRenderer: (params: any) => {
        const location = params.data as Location;
        return (
          <div className="flex justify-end w-full">
            <DropdownActions
              location={location}
              onEdit={onLocationEdit}
              onDelete={onLocationDelete}
              onAdjust={onLocationAdjust}
              onPrint={onLocationPrint}
              onToggleLock={onLocationToggleLock}
            />
          </div>
        );
      },
    });

    return baseColumns;
  }, [
    t,
    getLocationTypeTranslation,
    hasTeamData,
    onLocationEdit,
    onLocationDelete,
    onLocationAdjust,
    onLocationPrint,
    onLocationToggleLock,
  ]);

  // 🎯 Filtrar columnas basado en la selección del ColumnSelector
  const filteredColumnDefs: ColDef[] = useMemo(() => {
    // 🎯 Columnas que siempre deben estar visibles
    const alwaysVisibleColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      // Mantener siempre 'actions' y 'code' para evitar estados sin columnas de datos
      return field === 'actions' || field === 'code' || field === 'unknown';
    });

    // 🎯 Columnas seleccionables
    const selectableColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    });

    // Utilidad para evitar columnas duplicadas por `field`
    const dedupeByField = (cols: ColDef[]) => {
      const seen = new Set<string>();
      const result: ColDef[] = [];
      cols.forEach((c) => {
        const id = (c.field || c.headerName || 'unknown') as string;
        if (!seen.has(id)) {
          seen.add(id);
          result.push(c);
        }
      });
      return result;
    };

    if (selectedColumns.length === 0) {
      // Si no hay selección, mostrar todas las columnas seleccionables + las siempre visibles (sin duplicados)
      return dedupeByField([...selectableColumns, ...alwaysVisibleColumns]);
    }

    // Filtrar columnas seleccionables basado en la selección
    const selectedSelectableColumns = selectableColumns.filter((col) => {
      const fieldId = col.field || col.headerName || 'unknown';
      return selectedColumns.includes(fieldId);
    });

    // Combinar columnas seleccionadas + siempre visibles SIN duplicados
    const combined = [...selectedSelectableColumns];
    alwaysVisibleColumns.forEach((c) => {
      const field = c.field || c.headerName || 'unknown';
      const exists = combined.some((x) => (x.field || x.headerName || 'unknown') === field);
      if (!exists) combined.push(c);
    });

    return combined;
  }, [allColumnDefs, selectedColumns]);

  // 🎯 Columnas para el ColumnSelector (formato TanStack)
  const tanStackColumns: ColumnDef<Location>[] = useMemo(() => {
    return convertToTanStackColumns(allColumnDefs);
  }, [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
